import { BlockRounded, DeleteRounded, SettingsBackupRestoreRounded, SwapHorizRounded, SystemUpdateAltRounded } from '@mui/icons-material'
import { Card, CardContent, Checkbox, FormControlLabel, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import { Box } from '@mui/system'
import { memo, useLayoutEffect, useRef, useState } from 'react'
import { Handle, NodeToolbar, Position, useReactFlow, useStore } from 'reactflow'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import ChangeTechnologyDialog from '../dialogs/ChangeTechnologyDialog'
import SkipNodeDialog from '../dialogs/SkipNodeDialog'
import { resolveNodeToolbarPosition, resolveToolbarHorizontalShift, resolveVisibleBounds } from './nodeToolbarPlacement'
import { JOB_HIGHLIGHT_COLOR } from '../bomTree'
import { isExternalEffective } from '../jobExternalCharacter'
import { NODE_CARD_SX, NodeCardLines, WORKFLOW_NODE_BACKGROUND_COLOR } from './NodeCardFace'
import { systemValueLabel } from '../../../generic/systemValues/systemValueLabel'

// Story 204 (P11): a JOB card names its technology by code (`data.technology`), like every other screen; the raw
// `referenceName` stays in the data because the canvas numbers new jobs with it (`Assembly-1`) and saves it.
const technologyLine = (t_, data) => (data.nodeType === 'JOB' && data.technology && data.technology.code
  ? systemValueLabel(t_, 'technology', data.technology)
  : data.referenceName)

/**
 * @typedef {import('../../../generic/utilities/objectStructures/workflow/workflow').TechnologyNodeData} TechnologyNodeData TechnologyNodeData object
 */

// S33 (RN10.1, ajuste PO 23/07/2026): the yellow fill made the node's white text hard to read.
// A selected node now shows a MIC-green border and, on JOB nodes, MIC-green text; WORKFLOW
// (subworkflow) nodes — whose resting fill is already the MIC green — switch to a stronger green
// fill instead. Same values as the dark-theme brand palette (primary/secondary.darker),
// hardcoded like the WORKFLOW resting color below so the light theme (orange primary) never
// repaints the selection.
const SELECTED_NODE_COLOR = '#7FE1A7'
const SELECTED_NODE_BORDER = '3px solid ' + SELECTED_NODE_COLOR
const SELECTED_WORKFLOW_NODE_BACKGROUND_COLOR = '#05fd92'

// S31/S32 (RN09.4/RN09.5/D5, ajuste PO 23/07/2026): clicking a job chip in the BOM highlights its
// node (or its container subworkflow node, S32) with a border of its own — same technique as the
// selection border above but in blue, so both remain distinguishable (D5). Lower priority than
// selection: if a node happens to be both selected and job-highlighted, selection wins. The color
// is shared with the BOM tree row the click came from (`bomTree.js#JOB_HIGHLIGHT_COLOR`).
const JOB_HIGHLIGHT_BORDER = '3px solid ' + JOB_HIGHLIGHT_COLOR

// S69-S71 (CA14/RN30/RN31/RN31.1, DN-5): an effectively external JOB node — subcontracted work — is
// distinguished with a purple, a family so far unused in this editor. It goes in the BACKGROUND channel,
// while selection and the BOM highlight above go in the BORDER channel, so they COEXIST: an external node
// that is selected shows BOTH signals and the configurator never loses sight of which nodes leave the
// shop. There is no priority ladder between them, because they do not compete for the same channel.
//
// Hardcoded like every other colour in this file, so the light theme (whose primary is orange) cannot
// repaint it. The background and the text are chosen as a PAIR rather than inherited: white on this purple
// clears WCAG AA by a wide margin, which is what keeps the node legible in BOTH themes (RN31.1). A test
// computes that contrast instead of trusting the eye, so a later "nicer" purple that broke legibility
// fails there rather than shipping.
export const EXTERNAL_JOB_NODE_BACKGROUND_COLOR = '#6A1B9A'
export const EXTERNAL_JOB_NODE_TEXT_COLOR = '#FFFFFF'

/**
 * Keeps the node toolbar fully visible inside the reactflow pane horizontally. It only mounts
 * while the toolbar is open, subscribes to the graph transform so it re-measures on every
 * pan/zoom (the toolbar children are otherwise skipped by React when only the wrapper moves),
 * and corrects the toolbar wrapper with an exact, measured margin instead of an estimate.
 */
function KeepToolbarInPane ({ children }) {
  const innerRef = useRef(null)
  // Re-render (and so re-measure) whenever the graph pans or zooms while the toolbar is open.
  const transform = useStore((store) => store.transform)

  useLayoutEffect(() => {
    const inner = innerRef.current
    const wrapper = inner ? inner.closest('.react-flow__node-toolbar') : null
    const pane = inner ? inner.closest('.react-flow') : null
    if (!wrapper || !pane) {
      return
    }
    // Overlays docked over the pane edges (technologies palette / its opener) narrow the area
    // where the toolbar is actually visible. They live in an ancestor of the pane, so walk up a
    // few levels until they are found.
    let container = pane.parentElement
    for (let depth = 0; depth < 4 && container && !container.querySelector('[data-node-toolbar-obstacle]'); depth++) {
      container = container.parentElement
    }
    const obstacles = container ? Array.from(container.querySelectorAll('[data-node-toolbar-obstacle]')) : []
    const bounds = resolveVisibleBounds(pane.getBoundingClientRect(), obstacles.map((obstacle) => obstacle.getBoundingClientRect()))
    wrapper.style.marginLeft = ''
    const shift = resolveToolbarHorizontalShift(wrapper.getBoundingClientRect(), bounds)
    if (shift !== 0) {
      wrapper.style.marginLeft = shift + 'px'
    }
  }, [transform])

  return <div ref={innerRef}>{children}</div>
}

/**
 * @param {Object} param
 * @param {string} param.id Node id
 * @param {TechnologyNodeData} param.data Node data object
 * @returns
 */

function TechnologyNode ({ id, data }) {
  const handleSize = '0.6rem'
  // S69/S74/RN32: only JOB nodes, and only through the single owner of the predicate — so the colour can
  // never disagree with the Extern checkbox or the five disabled fields for the same node. The node data
  // carries both facts it needs, `external` and `technology`, straight from Sun.
  const externallyEffectiveJob = data.nodeType === 'JOB' && isExternalEffective(data)

  const okStyle = { top: '20%', background: 'green', width: handleSize, height: handleSize }
  const noOkStyle = { top: '80%', background: 'red', width: handleSize, height: handleSize }
  const { t_ } = useSmartTranslation()

  const { setNodes, getNodes, setEdges } = useReactFlow()
  const [changeTechnologyDialogOpen, setChangeTechnologyDialogOpen] = useState(false)
  const [skipDialogOpen, setSkipDialogOpen] = useState(false)

  // The toolbar overflows the pane (and gets clipped) when the node is near the top of the
  // canvas, so it flips below the node in that case; the horizontal overflow near the side edges
  // is corrected by KeepToolbarInPane with exact DOM measurements. The selector returns a
  // primitive so the node only re-renders when the resolved side actually changes.
  const toolbarPosition = useStore((store) => {
    const node = store.nodeInternals.get(id)
    const [, translateY, zoom] = store.transform
    const nodeScreenY = (node?.positionAbsolute?.y || 0) * zoom + translateY
    return resolveNodeToolbarPosition(nodeScreenY)
  })

  // S31 (RN17/D7): on a non-operable FabricationOrder (closed/cancelled) neither Skip nor
  // Reactivate are offered. Templates/subworkflows (no FabricationOrder) default to operable.
  const fabricationOrderOperable = data.fabricationOrderOperable !== false
  // S26/S31: Skip is offered on non-skipped JOB nodes while the FabricationOrder is operable.
  const showSkipAction = data.nodeType === 'JOB' && !data.skipped && fabricationOrderOperable
  // Historia 100 (S137, RN43/POL1, cita #86/S60+S62 -- C10): a dispatched external-circuit job
  // cannot be skipped until it is returned -- Superficie 5 of that historia.
  const skipBlockedByDispatch = data.dispatched === true
  // S27 (CA2): the job cannot be skipped once it started or has reports.
  const skipActionDisabled = !data.editing || data.hasParts || skipBlockedByDispatch
  // S30/S31: Reactivate is offered on a skipped JOB node while the FabricationOrder is operable.
  const showReactivateAction = data.nodeType === 'JOB' && data.skipped && fabricationOrderOperable
  const reactivateActionDisabled = !data.editing

  // S54/RN01.2/RNT5: the historical free-text editor now edits the node "code" (the functional
  // identifier, renamed from the old "name" on the backend).
  const onNodeCodeChange = (newValue) => {
    const newNodeValues = getNodes().map((node) => {
      if (node.id === id) {
        node.style = { ...node.style, borderColor: '', borderWidth: '', borderStyle: '' }
        node.data = { ...node.data }
        node.data.nodeCode = newValue
      }
      return node
    })

    if (typeof data.updateNodeCode === 'function') {
      data.updateNodeCode(newValue)
    }
    setNodes(newNodeValues)
  }

  // S5/S54 (RN01.2/RNT5): new free, descriptive "name", independent from the "code".
  const onNodeNameChange = (newValue) => {
    const newNodeValues = getNodes().map((node) => {
      if (node.id === id) {
        node.data = { ...node.data }
        node.data.nodeName = newValue
      }
      return node
    })

    if (typeof data.updateNodeName === 'function') {
      data.updateNodeName(newValue)
    }
    setNodes(newNodeValues)
  }

  const onReworkChange = (newValue) => {
    const newNodeValues = getNodes().map((node) => {
      if (node.id === id) {
        node.data = { ...node.data }
        node.data.rework = newValue
      }
      return node
    })

    if (typeof data.updateNodeRework === 'function') {
      data.updateNodeRework(newValue)
    }
    setNodes(newNodeValues)
  }

  const onDeleteNode = () => {
    setNodes((node) => node.filter((e) => e.id !== id))
    setEdges((edge) => edge.filter((e) => e.source !== id && e.target !== id))
    if (typeof data.deleteNode === 'function') {
      data.deleteNode(id)
    }
  }

  const onTechnologyChange = (newTechnology) => {
    const newNodeValues = getNodes().map((node) => {
      if (node.id === id) {
        node.data = { ...node.data }
        node.data.referenceName = newTechnology.name
        node.data.technology = newTechnology
      }
      return node
    })

    if (typeof data.updateNodeTechnology === 'function') {
      data.updateNodeTechnology(newTechnology)
    }
    setNodes(newNodeValues)
  }

  const onSkipNode = (skipReason) => {
    const newNodeValues = getNodes().map((node) => {
      if (node.id === id) {
        node.data = { ...node.data, skipped: true, skipReason }
      }
      return node
    })

    if (typeof data.updateNodeSkip === 'function') {
      data.updateNodeSkip(skipReason)
    }
    setNodes(newNodeValues)
  }

  const onReactivateNode = () => {
    const newNodeValues = getNodes().map((node) => {
      if (node.id === id) {
        node.data = { ...node.data, skipped: false, skipReason: '' }
      }
      return node
    })

    if (typeof data.updateNodeReactivate === 'function') {
      data.updateNodeReactivate()
    }
    setNodes(newNodeValues)
  }

  const skipTraceTitle = data.skipped
    ? [
        t_('Skipped'),
        data.skippedBy ? t_('Skipped by') + ': ' + data.skippedBy : '',
        data.skippedAt ? t_('Date') + ': ' + data.skippedAt : '',
        data.skipReason ? t_('Reason') + ': ' + data.skipReason : ''
      ].filter(Boolean).join(' — ')
    : ''

  return (
    <>
      <NodeToolbar position={toolbarPosition === 'bottom' ? Position.Bottom : Position.Top} onMouseDownCapture={(e) => e.stopPropagation()}>
        <KeepToolbarInPane>
          <Card sx={{ backgroundColor: (theme) => theme.palette.background.card }}>
            <CardContent sx={{ paddingBottom: '0px !Important' }}>
              <Stack spacing={1}>
                <TextField
                  label={t_('Code')}
                  required
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={data.nodeCode}
                  onChange={(event) => onNodeCodeChange(event.target.value)}
                  error={!data.nodeCode?.trim()}
                  helperText={!data.nodeCode?.trim() ? t_('Code is required') : ''}
                  disabled={!data.editing}
                />
                <TextField
                  label={t_('Name')}
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={data.nodeName || ''}
                  onChange={(event) => onNodeNameChange(event.target.value)}
                  disabled={!data.editing}
                />
              </Stack>
              <Stack>
                {
            data.nodeType === 'JOB'
              ? <FormControlLabel
                  disabled={!data.editing}
                  control={
                    <Checkbox
                      checked={data.rework}
                      onChange={(event) => onReworkChange(event.target.checked)}
                    />
                }
                  label={t_('REWORKED').charAt(0).toUpperCase() + t_('REWORKED').substring(1).toLowerCase()}
                />
              : null
              }
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center'
                }}
                >
                  <IconButton
                    disabled={data.workflowInUse || !data.editing}
                    color='error' onClick={() => {
                      onDeleteNode()
                    }}
                  >
                    <DeleteRounded />
                  </IconButton>
                  {data.nodeType === 'JOB'
                    ? (
                      <Tooltip title={data.hasParts ? t_('The job has already started and its technology cannot be changed') : t_('Change technology')}>
                        <span>
                          <IconButton
                            aria-label={t_('Change technology')}
                            color='primary'
                            disabled={!data.editing || data.hasParts}
                            onClick={() => setChangeTechnologyDialogOpen(true)}
                          >
                            <SwapHorizRounded />
                          </IconButton>
                        </span>
                      </Tooltip>
                      )
                    : null}
                  {showSkipAction
                    ? (
                      <Tooltip title={skipBlockedByDispatch ? t_('Cannot be skipped while dispatched') : data.hasParts ? t_('The job has already started and cannot be skipped') : t_('Skip')}>
                        <span>
                          <IconButton
                            aria-label={t_('Skip')}
                            color='primary'
                            disabled={skipActionDisabled}
                            onClick={() => setSkipDialogOpen(true)}
                          >
                            <BlockRounded />
                          </IconButton>
                        </span>
                      </Tooltip>
                      )
                    : null}
                  {showReactivateAction
                    ? (
                      <Tooltip title={t_('Reactivate')}>
                        <span>
                          <IconButton
                            aria-label={t_('Reactivate')}
                            color='primary'
                            disabled={reactivateActionDisabled}
                            onClick={onReactivateNode}
                          >
                            <SettingsBackupRestoreRounded />
                          </IconButton>
                        </span>
                      </Tooltip>
                      )
                    : null}
                </div>
              </Stack>
            </CardContent>
          </Card>
        </KeepToolbarInPane>
      </NodeToolbar>
      <ChangeTechnologyDialog
        open={changeTechnologyDialogOpen}
        onClose={() => setChangeTechnologyDialogOpen(false)}
        currentTechnology={data.technology}
        onAccept={onTechnologyChange}
      />
      <SkipNodeDialog
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        onAccept={onSkipNode}
      />
      <div>
        <Handle
          id='target'
          type='target'
          className='left'
          position={Position.Left}
          style={{ zIndex: 10000, width: handleSize, height: handleSize }}
          isConnectable={data.isConnectable}
        />
        <Tooltip title={skipTraceTitle}>
          <Card
            data-testid='technology-node-card'
            sx={{
              ...NODE_CARD_SX,
              // S33 (RN10.1, ajuste PO): selection is shown with a MIC-green border plus green
              // text on JOB nodes, and a stronger green fill with black text on WORKFLOW nodes
              // (white on the bright green was hard to distinguish); it takes precedence over the
              // job highlight (S31/S32, blue border only) and over the WORKFLOW resting color.
              border: data.selected ? SELECTED_NODE_BORDER : data.jobHighlighted ? JOB_HIGHLIGHT_BORDER : undefined,
              // S69/S70/S71: the purple owns the BACKGROUND, so it never touches the border above and both
              // signals show at once. The text colour goes with it: since the background changed, the pair
              // chosen for contrast wins over the selection's green tint, and the selection stays perfectly
              // visible in its own channel. RN32: only JOB nodes — a subworkflow container has no
              // technology of its own and keeps its resting green.
              color: externallyEffectiveJob
                ? EXTERNAL_JOB_NODE_TEXT_COLOR
                : data.selected ? (data.nodeType === 'WORKFLOW' ? '#000' : SELECTED_NODE_COLOR) : undefined,
              backgroundColor: externallyEffectiveJob
                ? EXTERNAL_JOB_NODE_BACKGROUND_COLOR
                : data.selected
                  ? data.nodeType === 'WORKFLOW' ? SELECTED_WORKFLOW_NODE_BACKGROUND_COLOR : undefined
                  : data.nodeType === 'WORKFLOW' ? WORKFLOW_NODE_BACKGROUND_COLOR : undefined
            }}
            style={{
              opacity: data.skipped ? 0.5 : 1,
              textDecoration: data.skipped ? 'line-through' : 'none'
            }}
          >
            {/* Historia 152 (S86/S87 · CA3.6, RN-03.9, §7.2 decisión nº 6, impacto R10): el
                resaltado OPCIONAL por proceso de origen, apagado por defecto. Es el CUARTO canal
                visual del nodo y por eso es una BANDA propia: el borde ya es de la selección (que
                manda) y del resaltado azul de BOM, y el fondo, del nodo externo y del relleno de
                los nodos WORKFLOW. Sin color no se pinta nada y el nodo vuelve exactamente a su
                presentación normal (el interruptor es una capa de lectura, `IN14`).
                J8: la banda va POSICIONADA sobre el borde superior de la tarjeta, fuera del flujo:
                como bloque en el flujo empujaba el contenido hacia abajo y dejaba un hueco en blanco
                en la parte alta de los nodos coloreados, que ya no quedaban centrados como los demás. */}
            {data.processOriginColor
              ? (
                <Box
                  data-testid='node-process-origin-band'
                  sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '0.35rem', backgroundColor: data.processOriginColor }}
                />
                )
              : null}
            {/* Story 224 (S111, H8): a newer version of the node's template is available. Patched into `data` by `Workflow`. */}
            {data.templateUpdateAvailable === true
              ? (
                <Tooltip title={t_('A newer version of the template is available')}>
                  <Box component='span' data-testid='node-template-update-mark' sx={{ position: 'absolute', top: 2, right: 4, display: 'inline-flex' }}>
                    <SystemUpdateAltRounded fontSize='small' color='info' />
                  </Box>
                </Tooltip>
                )
              : null}
            <NodeCardLines referenceName={technologyLine(t_, data)} nodeCode={data.nodeCode} nodeName={data.nodeName} />
          </Card>
        </Tooltip>
        <Handle
          id='OK'
          type='source'
          position={Position.Right}
          isConnectable={data.isConnectable}
          style={{
            ...okStyle,
            top: data.severalLinks ? '45%' : '20%',
            transform: data.severalLinks ? 'none' : 'translateY(-45%)'
          }}
        />

        {!data.severalLinks && (
          <Handle
            id='FAIL'
            type='source'
            position={Position.Right}
            isConnectable={data.isConnectable}
            style={{
              ...noOkStyle,
              top: '80%'
            }}
          />
        )}
      </div>
    </>
  )
}

export default memo(TechnologyNode)
